import { Plus, ImageOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { memo, useState, useEffect } from "react";
import LikeButton from "@/components/LikeButton";
import { ScheduleTag } from "@/components/ScheduleTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image cache + tiny pub/sub - shared across all components
const imageCache = new Map();
const subscribers = new Map(); // itemId -> Set<callback>
let preloadingInProgress = false;
let preloadQueue = [];

const setCachedImage = (itemId, dataUrl) => {
  imageCache.set(itemId, dataUrl);
  const subs = subscribers.get(itemId);
  if (subs) subs.forEach((cb) => cb(dataUrl));
};

const subscribe = (itemId, callback) => {
  if (!subscribers.has(itemId)) subscribers.set(itemId, new Set());
  subscribers.get(itemId).add(callback);
  return () => {
    const subs = subscribers.get(itemId);
    if (subs) {
      subs.delete(callback);
      if (subs.size === 0) subscribers.delete(itemId);
    }
  };
};

// Preload ALL images in batches (background task)
export const preloadAllImages = async (itemIds) => {
  if (preloadingInProgress) {
    preloadQueue = [...new Set([...preloadQueue, ...itemIds])];
    return;
  }
  
  preloadingInProgress = true;
  const uncachedIds = itemIds.filter(id => !imageCache.has(id));
  
  if (uncachedIds.length === 0) {
    preloadingInProgress = false;
    return;
  }
  
  // Process in batches of 10 for efficiency
  const batchSize = 10;
  for (let i = 0; i < uncachedIds.length; i += batchSize) {
    const batch = uncachedIds.slice(i, i + batchSize);
    
    try {
      const response = await fetch(`${API}/menu/images/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: batch })
      });
      
      if (response.ok) {
        const images = await response.json();
        Object.entries(images).forEach(([id, imageData]) => {
          if (imageData) {
            setCachedImage(id, imageData);
          }
        });
      }
    } catch (error) {
      console.error("Image batch preload failed:", error);
    }
    
    // Small delay between batches
    if (i + batchSize < uncachedIds.length) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }
  
  preloadingInProgress = false;
  
  if (preloadQueue.length > 0) {
    const queued = [...preloadQueue];
    preloadQueue = [];
    preloadAllImages(queued);
  }
};

export const getCachedImage = (itemId) => imageCache.get(itemId);
export const isImageCached = (itemId) => imageCache.has(itemId);

/**
 * Resolves an item's image via 3 strategies (in order):
 *   1. Synchronous cache hit (instant)
 *   2. Subscribe to cache - resolves when batch preload arrives
 *   3. Fallback: direct fetch after 2s if no batch arrived
 * No polling. One subscription per card. Cleans up on unmount.
 */
const useCachedImage = (itemId) => {
  const [imageUrl, setImageUrl] = useState(() => imageCache.get(itemId) || null);
  const [imageLoading, setImageLoading] = useState(!imageCache.has(itemId));
  const [version, setVersion] = useState(0);

  // Listen for admin image updates → drop stale cache + refetch
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.itemId === itemId) {
        imageCache.delete(itemId);
        setImageUrl(null);
        setImageLoading(true);
        setVersion((v) => v + 1);
      }
    };
    window.addEventListener("menu-image-updated", handler);
    return () => window.removeEventListener("menu-image-updated", handler);
  }, [itemId]);

  useEffect(() => {
    if (imageCache.has(itemId)) {
      setImageUrl(imageCache.get(itemId));
      setImageLoading(false);
      return undefined;
    }

    let cancelled = false;

    const unsubscribe = subscribe(itemId, (dataUrl) => {
      if (cancelled) return;
      setImageUrl(dataUrl);
      setImageLoading(false);
    });

    // Fallback: direct fetch if batch preload hasn't delivered after 2s
    const fallback = setTimeout(() => {
      if (cancelled || imageCache.has(itemId)) return;
      fetch(`${API}/menu/${itemId}/image`)
        .then((res) => res.json())
        .then((data) => {
          if (cancelled) return;
          if (data.image_base64) setCachedImage(itemId, data.image_base64);
        })
        .catch((err) => console.error("Image fallback fetch failed:", err))
        .finally(() => {
          if (!cancelled) setImageLoading(false);
        });
    }, 2000);

    return () => {
      cancelled = true;
      unsubscribe();
      clearTimeout(fallback);
    };
  }, [itemId, version]);

  return { imageUrl, imageLoading };
};

// Desktop/Tablet Card - Vertical layout
const MenuCard = memo(function MenuCard({ item, categoryColor, onAddToBasket, viewOnly = false, locationSlug = null, accessCode = null }) {
  const { imageUrl, imageLoading } = useCachedImage(item.id);

  const displayPrice = item.variants && item.variants.length > 0
    ? Math.min(...item.variants.map(v => v.price))
    : item.price;

  const hasVariants = item.variants && item.variants.length > 0;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-200 h-full flex flex-col">
      {/* Image Section - No price badge */}
      <div className="relative aspect-[4/3] bg-gray-100">
        {/* Delivery-schedule tag — top-left, over the image */}
        <div className="absolute top-2 left-2 z-10">
          <ScheduleTag value={item.delivery_schedule} variant="card" />
        </div>
        {locationSlug && accessCode && (
          <LikeButton
            slug={locationSlug}
            itemId={item.id}
            accessCode={accessCode}
            initialLiked={item.liked_by_me}
            initialCount={item.like_count}
          />
        )}
        {imageLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1aa59a] rounded-full animate-spin" />
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <ImageOff className="w-10 h-10 text-gray-300" />
          </div>
        )}
      </div>

      {/* Content Section */}
      <CardContent className="p-3 flex-1 flex flex-col">
        <h3 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-2">
          {item.name}
        </h3>

        {/* Price directly below name */}
        <p className="font-bold text-base mb-2" style={{ color: categoryColor || '#1aa59a' }}>
          {hasVariants ? `From £${displayPrice.toFixed(2)}` : `£${displayPrice.toFixed(2)}`}
        </p>
        
        {item.description && (
          <p className="text-xs text-gray-500 line-clamp-2 mb-2 flex-1">
            {item.description}
          </p>
        )}

        {!viewOnly && (
          <Button
            onClick={() => onAddToBasket(item)}
            className="w-full mt-auto text-white text-sm py-2"
            style={{ backgroundColor: categoryColor || '#1aa59a' }}
            data-testid={`add-to-basket-${item.id}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add to Basket
          </Button>
        )}
      </CardContent>
    </Card>
  );
});

// Mobile Card - Compact vertical layout for 2-column grid
export const MenuCardMobile = memo(function MenuCardMobile({ item, categoryColor, onAddToBasket, viewOnly = false, locationSlug = null, accessCode = null }) {
  const { imageUrl, imageLoading } = useCachedImage(item.id);

  const displayPrice = item.variants && item.variants.length > 0
    ? Math.min(...item.variants.map(v => v.price))
    : item.price;

  const hasVariants = item.variants && item.variants.length > 0;

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
      {/* Square image for mobile */}
      <div className="relative aspect-square bg-gray-100">
        {/* Delivery-schedule tag — top-left, over the image */}
        <div className="absolute top-1.5 left-1.5 z-10">
          <ScheduleTag value={item.delivery_schedule} variant="card" />
        </div>
        {locationSlug && accessCode && (
          <LikeButton
            slug={locationSlug}
            itemId={item.id}
            accessCode={accessCode}
            initialLiked={item.liked_by_me}
            initialCount={item.like_count}
          />
        )}
        {imageLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#1aa59a] rounded-full animate-spin" />
          </div>
        ) : imageUrl ? (
          <img src={imageUrl} alt={item.name} className="w-full h-full object-cover" loading="eager" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <ImageOff className="w-6 h-6 text-gray-300" />
          </div>
        )}
      </div>

      {/* Compact content */}
      <CardContent className="p-2 flex-1 flex flex-col">
        <h3 className="font-semibold text-gray-800 text-xs leading-tight line-clamp-2 mb-1">
          {item.name}
        </h3>

        {/* Price below name */}
        <p className="font-bold text-sm mb-2" style={{ color: categoryColor || '#1aa59a' }}>
          {hasVariants ? `From £${displayPrice.toFixed(2)}` : `£${displayPrice.toFixed(2)}`}
        </p>

        {!viewOnly && (
          <Button
            size="sm"
            onClick={() => onAddToBasket(item)}
            className="w-full text-white text-xs py-1.5 h-8 mt-auto"
            style={{ backgroundColor: categoryColor || '#1aa59a' }}
            data-testid={`add-to-basket-mobile-${item.id}`}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        )}
      </CardContent>
    </Card>
  );
});

export default MenuCard;
